import { MessageSquare, Mail, MessageCircle, Smartphone, Plus, ChevronDown, Settings2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'

// STATIC MOCKUP for /design approval — proposed brand "Channels" layout.
// A brand has many channels; each channel type declares its cardinality
// (one vs many per brand) and availability. Not wired to anything.

interface ChannelTypeMeta {
  key: string
  label: string
  icon: typeof MessageSquare
  multiple: boolean
  available: boolean
}

const CHANNEL_TYPES: ChannelTypeMeta[] = [
  { key: 'web_widget', label: 'Web widget', icon: MessageSquare, multiple: true, available: true },
  { key: 'email', label: 'Email', icon: Mail, multiple: false, available: false },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, multiple: true, available: false },
  { key: 'messenger', label: 'Messenger', icon: MessageCircle, multiple: false, available: false },
  { key: 'sms', label: 'SMS', icon: Smartphone, multiple: true, available: false },
]

const EXISTING = [
  { type: 'web_widget', name: 'Website widget', meta: 'Popup · marketing site' },
  { type: 'web_widget', name: 'App widget', meta: 'Drawer · in the SaaS app' },
]

export function ChannelsDemo() {
  return (
    <div className="max-w-3xl space-y-6">
      {/* Brand scope bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
        <span className="text-sm font-medium">Brand</span>
        <button className="flex h-8 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm">
          <span className="size-3 rounded-full bg-primary" />
          Demo
          <ChevronDown className="size-4 text-muted-foreground" />
        </button>
        <span className="text-xs text-muted-foreground">These channels belong to this brand.</span>
      </div>

      {/* Existing channels */}
      <div className="space-y-2">
        {EXISTING.map((ch) => {
          const meta = CHANNEL_TYPES.find((t) => t.key === ch.type)!
          const Icon = meta.icon
          return (
            <div key={ch.name} className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="flex items-center gap-3">
                <span className="flex size-9 items-center justify-center rounded-md bg-muted">
                  <Icon className="size-4" />
                </span>
                <div>
                  <p className="font-medium">{ch.name}</p>
                  <p className="text-xs text-muted-foreground">{ch.meta}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Active</Badge>
                <Button variant="ghost" size="sm" className="cursor-pointer">
                  <Settings2 className="size-4 mr-1" /> Configure
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Add a channel */}
      <div className="space-y-2">
        <p className="text-sm font-medium">Add a channel</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {CHANNEL_TYPES.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.key}
                disabled={!t.available}
                className={cn(
                  'flex items-center justify-between rounded-lg border border-border p-3 text-left disabled:opacity-60',
                  t.available && 'btn-glow-hover cursor-pointer hover:border-primary/50',
                )}
              >
                <span className="flex items-center gap-3">
                  <span className="flex size-9 items-center justify-center rounded-md bg-muted">
                    <Icon className="size-4" />
                  </span>
                  <span>
                    <span className="block text-sm font-medium">{t.label}</span>
                    <span className="block text-xs text-muted-foreground">
                      {t.multiple ? 'Multiple per brand' : 'One per brand'}
                    </span>
                  </span>
                </span>
                {t.available ? (
                  <span className="flex items-center gap-1 text-sm text-primary">
                    <Plus className="size-4" /> Add
                  </span>
                ) : (
                  <Badge variant="outline">Coming soon</Badge>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <Card>
        <CardContent className="py-3 text-xs text-muted-foreground">
          Concept: each <strong>channel type</strong> carries metadata for cardinality (one vs many per brand) and
          availability. Web widget allows several per brand (e.g. a different widget for the website vs the app); the
          help center stays one per brand. Other channel types appear here as they ship.
        </CardContent>
      </Card>
    </div>
  )
}
